import { ReactNode, RefObject, useState } from 'react';
import styled from 'styled-components';
import { useInfiniteScrolling } from 'utils/hooks/useInfiniteScrolling';

export const INFINITE_SCROLLING_TEST_ID = 'select-infinite-scrolling-loading-indicator';

const LoadingIndicatorWrapper = styled.div`
  width: 100%;
  align-items: center;
  padding: 10px;
  color: #c3c3c5;
`;

export type InfiniteScrollingProps = {
  as?: any; // overwriting for `undefined` ts error
  isOpen: boolean;
  canFetch: boolean;
  onFetch: () => void;
  children?: ReactNode;
  containerRef: RefObject<HTMLDivElement>;
  innerRef?: (el: HTMLElement | null) => void;
} & React.HTMLProps<HTMLDivElement>;

type LoadingIndicatorProps = Omit<InfiniteScrollingProps, 'isOpen'>;

const LoadingIndicator = ({
  containerRef,
  innerRef,
  children = 'Loading more...',
  canFetch,
  onFetch,
  ...rest
}: LoadingIndicatorProps) => {
  const [el, setEl] = useState<HTMLDivElement | null>(null);

  useInfiniteScrolling({
    container: containerRef,
    trigger: { current: el },
    canFetch,
    onFetch,
  });

  const setRef = (ref: HTMLDivElement | null) => {
    setEl(ref);
    if (innerRef) {
      innerRef(el);
    }
  };

  return (
    <LoadingIndicatorWrapper {...rest} ref={setRef} data-test={INFINITE_SCROLLING_TEST_ID}>
      {children}
    </LoadingIndicatorWrapper>
  );
};

export const InfiniteScrolling = (props: InfiniteScrollingProps) => {
  const { isOpen, canFetch, children, ...rest } = props;
  const shouldShowOptionListLoadingIndicator = isOpen && canFetch;

  if (!shouldShowOptionListLoadingIndicator) return null;

  return (
    <LoadingIndicator {...rest} canFetch={canFetch}>
      {children}
    </LoadingIndicator>
  );
};
